/* oxlint-disable jsx-a11y/no-noninteractive-element-to-interactive-role -- ARIA 1.2 combobox: <input role="combobox"> + <li role="option"> is the spec'd pattern */
import { useEffect, useId, useRef, useState } from 'react';
import { MapPin, X } from 'lucide-react';
import { cx } from '../../lib/util/cx';
import { useLocaleAutocomplete, useLocalesById } from './queries';
import { localeDetail, localeLabel, type LocaleSummary } from './api';

/**
 * Multi-locale filter (BE reqs §0.3, FE plan §3.7): fuzzy search over
 * `/admin/locales/autocomplete`, selections as chips, emits `localeIds`.
 * No selection = all locales. Chips for ids restored from the URL are named
 * via `/locales/{id}`.
 *
 * ARIA combobox: ↑/↓ move, Enter selects, Escape closes, Backspace on an
 * empty input removes the last chip.
 */
export function LocalePicker({
  value,
  onChange,
  placeholder = 'All locales',
}: {
  value: string[];
  onChange: (ids: string[]) => void;
  placeholder?: string;
}) {
  const id = useId();
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  // Locales picked in this session (from suggestions); ids restored from the
  // URL are named by the by-id queries below instead.
  const [picked, setPicked] = useState<Record<string, LocaleSummary>>({});

  useEffect(() => {
    const t = setTimeout(() => setDebounced(term), 250);
    return () => clearTimeout(t);
  }, [term]);

  const suggestions = useLocaleAutocomplete(debounced);
  const missing = value.filter((v) => !picked[v]);
  const byId = useLocalesById(missing);
  const known: Record<string, LocaleSummary> = { ...picked };
  byId.forEach((q, i) => {
    const localeId = missing[i];
    if (q.data && localeId) known[localeId] = q.data;
  });

  const options = (suggestions.data ?? []).filter((l) => !value.includes(l.id));
  const showList = open && term.trim().length > 0;

  const add = (locale: LocaleSummary) => {
    setPicked((p) => ({ ...p, [locale.id]: locale }));
    onChange([...value, locale.id]);
    setTerm('');
    setActive(0);
    inputRef.current?.focus();
  };
  const remove = (localeId: string) => onChange(value.filter((v) => v !== localeId));

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((a) => Math.min(a + 1, Math.max(options.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter' && showList && options[active]) {
      e.preventDefault();
      add(options[active]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    } else if (e.key === 'Backspace' && term === '' && value.length) {
      remove(value[value.length - 1]!);
    }
  };

  return (
    <div className="relative min-w-[260px] font-body">
      <div
        className="flex min-h-[38px] flex-wrap items-center gap-1.5 rounded-input border border-[rgba(143,68,32,.12)] bg-white px-2.5 py-1 focus-within:border-orange focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-orange"
        onClick={() => inputRef.current?.focus()}
        role="presentation"
      >
        <MapPin size={15} strokeWidth={2} aria-hidden="true" className="shrink-0 text-muted" />
        {value.map((localeId) => {
          const l = known[localeId];
          const label = l ? localeLabel(l) : 'Loading…';
          return (
            <span
              key={localeId}
              className="inline-flex items-center gap-1 rounded-pill bg-[rgba(143,68,32,.08)] py-0.5 pl-2.5 pr-1 text-[13px] text-ink"
            >
              {label}
              <button
                type="button"
                aria-label={`Remove ${label}`}
                onClick={(e) => {
                  e.stopPropagation();
                  remove(localeId);
                }}
                className="flex h-5 w-5 items-center justify-center rounded-full border-0 bg-transparent text-muted hover:bg-[rgba(143,68,32,.12)]"
              >
                <X size={12} aria-hidden="true" />
              </button>
            </span>
          );
        })}
        <input
          ref={inputRef}
          id={id}
          role="combobox"
          aria-expanded={showList}
          aria-controls={`${id}-list`}
          aria-autocomplete="list"
          aria-activedescendant={showList && options[active] ? `${id}-opt-${active}` : undefined}
          aria-label="Filter by locale"
          value={term}
          placeholder={value.length ? '' : placeholder}
          onChange={(e) => {
            setTerm(e.target.value);
            setOpen(true);
            setActive(0);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 120)}
          onKeyDown={onKeyDown}
          className="min-w-[80px] flex-1 border-0 bg-transparent py-1 text-sm text-ink outline-none"
        />
      </div>
      {showList && (
        <ul
          id={`${id}-list`}
          role="listbox"
          className="absolute left-0 right-0 top-[calc(100%+4px)] z-30 m-0 max-h-[280px] list-none overflow-y-auto rounded-app-card border border-panel-border bg-white p-1 shadow-lg"
        >
          {options.length === 0 ? (
            <li className="px-3 py-2.5 text-[13px] text-muted">
              {suggestions.isFetching ? 'Searching…' : 'No locales match.'}
            </li>
          ) : (
            options.map((l, i) => (
              <li
                key={l.id}
                id={`${id}-opt-${i}`}
                role="option"
                aria-selected={i === active}
                onMouseDown={(e) => {
                  e.preventDefault();
                  add(l);
                }}
                onMouseEnter={() => setActive(i)}
                className={cx('cursor-pointer rounded-app px-3 py-2', i === active && 'bg-[rgba(143,68,32,.06)]')}
              >
                <div className="text-sm font-semibold text-ink">{localeLabel(l)}</div>
                {localeDetail(l) && <div className="text-xs text-muted">{localeDetail(l)}</div>}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
